/**
 * The bowl footprint: the shape everything in the stadium is lofted around.
 *
 * The arena's own plan is not a rectangle and not an ellipse — it is a
 * rectangle with its four corners cut off by the 45-degree planes
 * (|x| + |y| = 8064; see src/physics/arena-data.ts). Every ring of stadium
 * structure has to follow that outline or the stands drift away from the wall
 * in the corners and hug it on the straights, which is exactly what a lazy
 * cylinder-shaped bowl looks like.
 *
 * OFFSETTING IS EXACT, not a scale. Scaling the outline by a factor moves the
 * straights and the corner cuts by different amounts; offsetting moves every
 * edge along its own normal by the same distance, which is what "the stand is
 * 400 uu behind the wall, all the way round" means. For this octagon that is
 * three numbers: hx + d, hz + d, and c + d*sqrt(2).
 *
 * PARAMETRIZATION. Points are addressed by (edge index, fraction along edge)
 * rather than by arc length, because arc length is not preserved by offsetting:
 * the corner edges grow and the straights stay the same length, so a fixed `t`
 * would shear the loft in the corners. Addressing by edge keeps every ring of a
 * loft aligned with every other one.
 *
 * Pure arithmetic. Nothing here imports Three.js.
 */

import { ARENA } from '../physics/constants';

/** A point on the outline plus the outward normal of the edge it lies on. */
export interface BowlPoint {
  /** THREE frame. `z` is -physics-y: blue's end is +z. */
  x: number;
  z: number;
  /** Unit outward normal (away from the pitch), in the xz plane. */
  nx: number;
  nz: number;
}

/** An address on the outline: which of the 8 edges, and how far along it. */
export interface BowlSample {
  edge: number;
  f: number;
}

const SQRT2 = Math.SQRT2;

/**
 * The 8 outline vertices at distance `offset` outside the arena wall, in
 * counter-clockwise order starting at (+x, +z).
 *
 * At `offset = 0` this reproduces the collision mesh's own plan exactly: the
 * corner vertices land at (4096, 3968) and (2944, 5120), i.e. 1152 uu in from
 * each wall, which is `ARENA_PLANES.cornerCathetusLength`.
 */
export const bowlOutline = (offset: number): [number, number][] => {
  const hx = ARENA.halfWidth + offset;
  const hz = ARENA.halfLength + offset;
  const c = ARENA.cornerPlaneIntercept + offset * SQRT2;
  // Where the 45-degree cut meets each straight.
  const cx = c - hz; // |x| at the point the cut meets the end wall
  const cz = c - hx; // |z| at the point the cut meets the side wall
  return [
    [hx, cz],
    [cx, hz],
    [-cx, hz],
    [-hx, cz],
    [-hx, -cz],
    [-cx, -hz],
    [cx, -hz],
    [hx, -cz],
  ];
};

/** Length of each edge of `bowlOutline(offset)`, in outline order. */
export const bowlEdgeLengths = (offset: number): number[] => {
  const v = bowlOutline(offset);
  const out: number[] = [];
  for (let i = 0; i < v.length; i++) {
    const a = v[i];
    const b = v[(i + 1) % v.length];
    out.push(Math.hypot(b[0] - a[0], b[1] - a[1]));
  }
  return out;
};

/** Perimeter of the outline at `offset`. */
export const bowlPerimeter = (offset: number): number =>
  bowlEdgeLengths(offset).reduce((n, l) => n + l, 0);

/**
 * `count` addresses spread evenly around the outline, allocated to edges in
 * proportion to their length at `offset`.
 *
 * The allocation is done once, at one reference offset, and then reused for
 * every ring of a loft — that is the whole point of returning addresses rather
 * than points.
 */
export const bowlSamples = (count: number, offset = 0): BowlSample[] => {
  const lengths = bowlEdgeLengths(offset);
  const total = lengths.reduce((n, l) => n + l, 0);
  const out: BowlSample[] = [];
  for (let i = 0; i < count; i++) {
    // Walk a fraction of the perimeter and record which edge it landed on.
    let d = (i / count) * total;
    let edge = 0;
    while (edge < lengths.length - 1 && d > lengths[edge]) {
      d -= lengths[edge];
      edge++;
    }
    out.push({ edge, f: lengths[edge] <= 0 ? 0 : Math.min(1, d / lengths[edge]) });
  }
  return out;
};

/** Resolve an address to a point on the outline at `offset`. */
export const bowlPoint = (offset: number, sample: BowlSample): BowlPoint => {
  const v = bowlOutline(offset);
  const i = ((sample.edge % v.length) + v.length) % v.length;
  const a = v[i];
  const b = v[(i + 1) % v.length];
  const x = a[0] + (b[0] - a[0]) * sample.f;
  const z = a[1] + (b[1] - a[1]) * sample.f;
  // Outward normal of a counter-clockwise edge in the xz plane. The outline is
  // wound counter-clockwise when viewed from +y (looking down), for which the
  // outward normal of edge (a -> b) is (dz, -dx) normalized.
  const dx = b[0] - a[0];
  const dz = b[1] - a[1];
  const len = Math.hypot(dx, dz) || 1;
  return { x, z, nx: dz / len, nz: -dx / len };
};

/**
 * Is a point strictly inside the arena's playing footprint?
 *
 * This is the test every piece of stadium furniture has to fail. It is the
 * plan-view collision boundary, so anything that passes it is inside the
 * volume the ball and the cars move in, and would be a solid object sitting on
 * the pitch.
 */
export const insideArenaFootprint = (x: number, z: number, margin = 0): boolean =>
  Math.abs(x) < ARENA.halfWidth - margin &&
  Math.abs(z) < ARENA.halfLength - margin &&
  Math.abs(x) + Math.abs(z) < ARENA.cornerPlaneIntercept - margin * SQRT2;

/**
 * How far outside the arena wall a point is, along the wall's own normal.
 * Negative inside. Used by the tests to assert clearances rather than just
 * "not inside", which would pass for something 1 uu off the wall.
 */
export const bowlOffsetOf = (x: number, z: number): number => {
  const ax = Math.abs(x);
  const az = Math.abs(z);
  return Math.max(
    ax - ARENA.halfWidth,
    az - ARENA.halfLength,
    (ax + az - ARENA.cornerPlaneIntercept) / SQRT2
  );
};

// ---------------------------------------------------------------------------
// Lofting
// ---------------------------------------------------------------------------

/** One point of a cross-section, swept around the bowl. */
export interface BowlProfilePoint {
  /** Distance outside the arena wall, uu. */
  offset: number;
  /** Height above the pitch, uu (THREE y). */
  y: number;
}

/**
 * Sweep a cross-section around the bowl and return non-indexed triangles.
 *
 * Winding: the profile runs inner-to-outer, and the triangles come out wound so
 * their front faces point UP and INWARD — toward the pitch, which is where the
 * camera always is. Anything that has to be seen from outside asks for
 * `DoubleSide` instead of a second loft.
 */
export const loftBowl = (
  profile: readonly BowlProfilePoint[],
  samples: readonly BowlSample[]
): Float32Array => {
  const rings = profile.length;
  const n = samples.length;
  if (rings < 2 || n < 3) return new Float32Array(0);

  // Precompute every ring's points once; `bowlPoint` rebuilds the outline per
  // call and this is O(rings * samples).
  const points: BowlPoint[][] = profile.map((p) => samples.map((s) => bowlPoint(p.offset, s)));

  const quads = (rings - 1) * n;
  const out = new Float32Array(quads * 18);
  let o = 0;
  const push = (ring: number, i: number): void => {
    const p = points[ring][i];
    out[o++] = p.x;
    out[o++] = profile[ring].y;
    out[o++] = p.z;
  };

  for (let r = 0; r < rings - 1; r++) {
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      // (r, i) (r, j) (r+1, j) / (r, i) (r+1, j) (r+1, i)
      push(r, i);
      push(r, j);
      push(r + 1, j);
      push(r, i);
      push(r + 1, j);
      push(r + 1, i);
    }
  }
  return out;
};

/**
 * A stepped seating deck: `rows` treads and risers between an inner front edge
 * and an outer back edge. Returned as a profile for `loftBowl`.
 */
export const steppedProfile = (
  frontOffset: number,
  frontY: number,
  rows: number,
  run: number,
  rise: number
): BowlProfilePoint[] => {
  // One polyline with no repeated points: a repeated point would loft a
  // zero-area quad ring, which is `samples` wasted triangles per row.
  const out: BowlProfilePoint[] = [{ offset: frontOffset, y: frontY }];
  for (let r = 0; r < rows; r++) {
    const offset = frontOffset + r * run;
    const y = frontY + r * rise;
    out.push({ offset: offset + run, y });
    out.push({ offset: offset + run, y: y + rise });
  }
  return out;
};

/** Where `steppedProfile` ends up: the back edge of the top row. */
export const steppedBack = (
  frontOffset: number,
  frontY: number,
  rows: number,
  run: number,
  rise: number
): BowlProfilePoint => ({
  offset: frontOffset + rows * run,
  y: frontY + rows * rise,
});
